import { clsx } from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';

import styles from './SecondaryNav.module.scss';
import { SecondaryNavContext } from './SecondaryNavContext';
import { SecondaryNavItem } from './SecondaryNavItem';
import { SecondaryNavList } from './SecondaryNavList';
import { SecondaryNavTitle } from './SecondaryNavTitle';

export type NavItemKey = string | number;

type RootProps = {
  /** What to render in the `SecondaryNav.Item`. */
  children: ReactNode;
  /** Optional property for overriding styles. */
  className?: string;
  /** Key of the item that should be selected on mount. */
  defaultSelectedItem?: NavItemKey | null;
  /** Key(s) of the section(s) that should be expanded on mount. */
  defaultExpandedKeys?: NavItemKey[];
  /** Currently selected item key (only use if controlling state) */
  selectedItem?: NavItemKey | null;
  /** Called when selection changes (only use if controlling state) */
  onSelectedItemChange?: (key: NavItemKey | null) => void | null;
  /** Currently expanded section keys (only use if controlling state) */
  expandedKeys?: NavItemKey[] | null;
  /** Called when expanded sections change (only use if controlling state) */
  onExpandedKeysChange?: (keys: NavItemKey[]) => void | null;
};

/** Collapsible navigation meant to be used beside `UniversalLayout`'s side navigation. */
export const SecondaryNavRoot = ({
  children,
  className,
  defaultSelectedItem = null,
  defaultExpandedKeys = [],
  selectedItem,
  onSelectedItemChange,
  expandedKeys,
  onExpandedKeysChange,
}: RootProps) => {
  const [localSelectedItem, setLocalSelectedItem] =
    useState(defaultSelectedItem);
  const [localExpandedKeys, setLocalExpandedKeys] =
    useState(defaultExpandedKeys);

  const isSelectedItemControlled = !!selectedItem;
  const isExpandedKeysControlled = !!expandedKeys;

  const currentSelectedItem = selectedItem ?? localSelectedItem;
  const currentExpandedKeys = expandedKeys ?? localExpandedKeys;

  const state = useMemo(
    () => ({
      selectedItemKey: currentSelectedItem,
      expandedSectionKeys: currentExpandedKeys,
      registerSelectedItemKey: (key: NavItemKey | null) => {
        onSelectedItemChange?.(key);
        if (!isSelectedItemControlled) {
          setLocalSelectedItem(key);
        }
      },
      registerExpandedSectionKeys: (keys: NavItemKey[]) => {
        onExpandedKeysChange?.(keys);
        if (!isExpandedKeysControlled) {
          setLocalExpandedKeys(keys);
        }
      },
    }),
    [
      currentSelectedItem,
      currentExpandedKeys,
      isSelectedItemControlled,
      isExpandedKeysControlled,
      onSelectedItemChange,
      onExpandedKeysChange,
    ]
  );

  return (
    <SecondaryNavContext.Provider value={state}>
      <nav className={clsx(styles.secondaryNav, className)} data-zds>
        {children}
      </nav>
    </SecondaryNavContext.Provider>
  );
};

export const SecondaryNav = Object.assign(SecondaryNavRoot, {
  Title: SecondaryNavTitle,
  Item: SecondaryNavItem,
  List: SecondaryNavList,
});

export default SecondaryNav;
